import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getOrganizationWithRelations } from '../../services/entityService';
import { CircularProgress, Typography, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Link, Button, Chip } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import PageContainer from '../../components/layout/PageContainer';
import { MESSAGES } from '../../constants/messages';

const OrganizationDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [organization, setOrganization] = React.useState<any>(null);
    const [vcFirms, setVCFirms] = React.useState<any[]>([]);
    const [angels, setAngels] = React.useState<any[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);

    React.useEffect(() => {
        if (!id) return;
        const fetchOrganizationAndRelations = async () => {
            try {
                const { organization, vcFirms, angels } = await getOrganizationWithRelations(id);
                setOrganization(organization);
                setVCFirms(vcFirms);
                setAngels(angels);
            } catch (err) {
                setError('Failed to fetch organization details');
            } finally {
                setLoading(false);
            }
        };
        fetchOrganizationAndRelations();
    }, [id]);

    const handleBack = () => {
        navigate(-1);
    };

    if (loading) {
        return <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh"><CircularProgress /></Box>;
    }
    if (error) {
        return <Typography color="error">{error}</Typography>;
    }
    if (!organization) {
        return <Typography>No organization found.</Typography>;
    }

    return (
        <PageContainer>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Button
                    startIcon={<ArrowBack />}
                    onClick={handleBack}
                    sx={{ mr: 2 }}
                >
                    Back
                </Button>
                <Typography variant="h4">
                    {organization.name} 
                </Typography>
                {organization.org_type && (
                    <Chip
                        label={organization.org_type.charAt(0).toUpperCase() + organization.org_type.slice(1)}
                        size="small"
                        sx={{ ml: 2 }}
                    />
                )}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {MESSAGES.PAGE_DISCLAIMER}
            </Typography>
            {organization.description && <Typography><strong>Description:</strong> {organization.description}</Typography>}
            {organization.industries && Array.isArray(organization.industries) && organization.industries.length > 0 && <Typography><strong>Industries:</strong> {organization.industries.join(', ')}</Typography>}
            {organization.website && (
                <Typography>
                    <strong>Website:</strong>{' '}
                    <span>
                        {(() => {
                            const url = organization.website.startsWith('http://') || organization.website.startsWith('https://')
                                ? organization.website
                                : `https://${organization.website}`;
                            return (
                                <Link href={url} target="_blank" rel="noopener noreferrer">
                                    {organization.website}
                                </Link>
                            );
                        })()}
                    </span>
                </Typography>
            )}

            {organization.main_hq && <Typography><strong>Main HQ:</strong> {organization.main_hq}</Typography>}
            {organization.locations && Array.isArray(organization.locations) && organization.locations.length > 0 && <Typography><strong>Locations:</strong> {organization.locations.join(', ')}</Typography>}
            {organization.total_funding && (
                <Typography>
                    <strong>Total Funding (USD):</strong>{" "}
                    {new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                    notation: "compact",
                    maximumFractionDigits: 1,
                    }).format(organization.total_funding)}
                </Typography>
            )}
            {organization.valuation && (
                <Typography>
                    <strong>Valuation (USD):</strong>{" "}
                    {new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                    notation: "compact",
                    maximumFractionDigits: 1,
                    }).format(organization.valuation)}
                </Typography>
            )}
            {organization.social_media && organization.social_media.length > 0 && (
                <Typography><strong>Social Media:</strong> {organization.social_media.map((social: string, index: number) => (
                    <span key={index}>
                        <Link href={social} target="_blank" rel="noopener noreferrer">{social}</Link>
                        {index < organization.social_media.length - 1 && ', '}
                    </span>
                ))}</Typography>
            )}
            {organization.phone_number && <Typography><strong>Phone Number:</strong> {organization.phone_number}</Typography>}
            {organization.contact_email && (
                <Typography><strong>Contact Email:</strong> {
                    <span>
                        <Link href={`mailto:${organization.contact_email}`}>{organization.contact_email}</Link>
                    </span>
                }</Typography>
            )}
            <Typography><strong>Operations in Israel:</strong> {organization.has_operations_in_israel ? 'Yes' : 'No'}</Typography>
            <Typography><strong>Connections to Unit 8200:</strong> {organization.has_8200_connections ? 'Yes' : 'No'}</Typography>
            {organization.org_type === 'startup' && organization.exited && (
                <Typography>
                    <strong>Exit Status:</strong>{' '}
                    {organization.exited === 'yes' ? 'Yes (Exited)' : organization.exited === 'no' ? 'No (Active)' : 'Unknown'}
                </Typography>
            )}
            {organization.acquirer && (
                <Typography><strong>Acquirer:</strong> {organization.acquirer}</Typography>
            )}
            {organization.fund_category && (
                <Typography><strong>Fund Category:</strong> {organization.fund_category}</Typography>
            )}
            {organization.name_alias && Array.isArray(organization.name_alias) && organization.name_alias.length > 0 && <Typography><strong>Also known as:</strong> {organization.name_alias.join(", ")}</Typography>}
            <Box mt={4}>
                <Typography variant="h6" gutterBottom>VC Firms Linked to this Organization</Typography>
                {vcFirms && vcFirms.length > 0 ? (
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Main HQ</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {vcFirms.map((vc: any) => (
                                    <TableRow key={vc.id}>
                                        <TableCell>
                                            <Link href={`/vc/${vc.id}`}>{vc.name}</Link>
                                        </TableCell>
                                        <TableCell>{vc.main_hq}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Typography>{MESSAGES.NO_VC_FIRMS_FOUND}</Typography>
                )}
            </Box>

            <Box mt={4}>
                <Typography variant="h6" gutterBottom>Angel Investors Linked to this Organization</Typography>
                {angels && angels.length > 0 ? (
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Investment Theme</TableCell>
                                    <TableCell>Main HQ</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {angels.map((angel: any) => (
                                    <TableRow key={angel.id}>
                                        <TableCell>
                                            <Link href={`/angel/{angel.id}`}>{angel.name}</Link>
                                        </TableCell>
                                        <TableCell>{angel.investment_theme}</TableCell>
                                        <TableCell>{angel.main_hq}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Typography>{MESSAGES.NO_ANGEL_FOUND}</Typography>
                )}
            </Box>

            <Box mt={4}>
                <Typography variant="h6" gutterBottom>Persons of Interest Linked to this Organization</Typography>
                {organization.pois && organization.pois.length > 0 ? (
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Position</TableCell>
                                    <TableCell>Description</TableCell>
                                    <TableCell>Military History</TableCell>
                                    <TableCell>Unit 8200</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {organization.pois.map((poi: any) => (
                                    <TableRow key={poi.id}>
                                        <TableCell>
                                            <Link href={`/poi/${poi.id}`}>{poi.name}</Link>
                                        </TableCell>
                                        <TableCell>{poi.position || 'N/A'}</TableCell>
                                        <TableCell>{poi.description}</TableCell>
                                        <TableCell>{poi.history_in_IDF || 'N/A'}</TableCell>
                                        <TableCell>{poi.connection_8200 ? 'Yes' : 'No'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Typography>No persons of interest found.</Typography>
                )}
            </Box>
        </PageContainer>
    );
};

export default OrganizationDetail;